import { Plus, X } from 'lucide-react';
import {
  OP_LABELS, VALUELESS_OPS, opsFor, searchTargets,
  type AdvancedQuery, type MetadataSchemaDefinition, type Rule, type RuleOp, type SearchTarget, type StratumDTO,
} from '@prism/contracts';

export const EMPTY_ADVANCED: AdvancedQuery = { in: [], match: 'all', rules: [] };

/** Rules that are complete enough to send. */
export function usableAdvanced(a: AdvancedQuery): AdvancedQuery | null {
  const rules = a.rules.filter((r) => VALUELESS_OPS.includes(r.op) || (r.value !== undefined && r.value !== '' && !(Array.isArray(r.value) && !r.value.length)
    && (r.op !== 'between' || (r.value2 !== undefined && r.value2 !== ''))));
  return a.in.length || rules.length ? { ...a, rules } : null;
}

export function advancedSummary(a: AdvancedQuery, targets: SearchTarget[]): string[] {
  const label = (id: string) => targets.find((t) => t.id === id)?.label ?? id;
  return [
    ...(a.in.length ? [`Text in ${a.in.map(label).join(', ')}`] : []),
    ...a.rules.map((r) => `${label(r.field)} ${OP_LABELS[r.op]}${VALUELESS_OPS.includes(r.op) ? '' : ` ${Array.isArray(r.value) ? r.value.join(' / ') : r.value ?? ''}${r.op === 'between' ? ` and ${r.value2 ?? ''}` : ''}`}`),
  ];
}

function ValueInput({ target, rule, onChange }: { target: SearchTarget; rule: Rule; onChange: (r: Rule) => void }) {
  if (VALUELESS_OPS.includes(rule.op)) return <span />;
  if ((target.kind === 'vocabulary' && (rule.op === 'is' || rule.op === 'is_not'))) {
    const list = Array.isArray(rule.value) ? rule.value : [];
    return (
      <div className="tags" style={{ gridColumn: 'span 2' }}>{target.options?.map((o) => (
        <button type="button" key={o.value} className={`tag toggle ${list.includes(o.value) ? 'on' : ''}`} aria-pressed={list.includes(o.value)}
          onClick={() => onChange({ ...rule, value: list.includes(o.value) ? list.filter((v) => v !== o.value) : [...list, o.value] })}>{o.label}</button>
      ))}</div>
    );
  }
  const type = target.kind === 'number' ? 'number' : target.kind === 'date' || target.kind === 'datetime' ? 'date' : 'text';
  const one = <input className="input" type={type} value={String(rule.value ?? '')} aria-label="Value" onChange={(e) => onChange({ ...rule, value: e.target.value })} />;
  if (rule.op === 'between') {
    return <div style={{ display: 'flex', gap: 4, alignItems: 'center', gridColumn: 'span 2' }}>{one}<span className="faint">and</span>
      <input className="input" type={type} value={String(rule.value2 ?? '')} aria-label="Second value" onChange={(e) => onChange({ ...rule, value2: e.target.value })} /></div>;
  }
  return one;
}

/** Choose where the text query searches, and add conditions on fields. */
export function AdvancedSearchPanel({ value, onChange, schema, strata }: {
  value: AdvancedQuery; onChange: (a: AdvancedQuery) => void; schema: MetadataSchemaDefinition | undefined; strata: StratumDTO[];
}) {
  const targets = searchTargets(schema, strata);
  const set = (patch: Partial<AdvancedQuery>) => onChange({ ...value, ...patch });
  const toggleIn = (id: string) => set({ in: value.in.includes(id) ? value.in.filter((x) => x !== id) : [...value.in, id] });
  const setRule = (i: number, r: Rule) => set({ rules: value.rules.map((x, j) => (j === i ? r : x)) });
  return (
    <div className="advanced-search" data-testid="advanced-search">
      <div className="field">
        <label>Search the text in</label>
        <div className="tags">
          <button type="button" className={`tag toggle ${value.in.length === 0 ? 'on' : ''}`} aria-pressed={value.in.length === 0} onClick={() => set({ in: [] })}>Everywhere</button>
          {targets.map((t) => (
            <button type="button" key={t.id} className={`tag toggle ${value.in.includes(t.id) ? 'on' : ''}`} aria-pressed={value.in.includes(t.id)} onClick={() => toggleIn(t.id)} data-testid={`in-${t.id}`}>{t.label}</button>
          ))}
        </div>
      </div>
      <div className="field">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ margin: 0 }}>Conditions</label>
          {value.rules.length > 1 && (
            <div className="segmented" role="radiogroup" aria-label="Match">
              <button aria-pressed={value.match === 'all'} onClick={() => set({ match: 'all' })}>All must match</button>
              <button aria-pressed={value.match === 'any'} onClick={() => set({ match: 'any' })}>Any may match</button>
            </div>
          )}
        </div>
        {value.rules.map((r, i) => {
          const target = targets.find((t) => t.id === r.field) ?? targets[0]!;
          return (
            <div key={i} className="adv-rule" data-testid="adv-rule">
              <select className="select" value={r.field} aria-label="Field" onChange={(e) => {
                const t = targets.find((x) => x.id === e.target.value)!;
                setRule(i, { field: t.id, op: opsFor(t.kind)[0]! });
              }}>
                {targets.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
              <select className="select" value={r.op} aria-label="Condition" onChange={(e) => setRule(i, { field: r.field, op: e.target.value as RuleOp })}>
                {opsFor(target.kind).map((op) => <option key={op} value={op}>{OP_LABELS[op]}</option>)}
              </select>
              <ValueInput target={target} rule={r} onChange={(nr) => setRule(i, nr)} />
              <button className="btn ghost sm icon" aria-label="Remove condition" onClick={() => set({ rules: value.rules.filter((_, j) => j !== i) })}><X size={13} /></button>
            </div>
          );
        })}
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn ghost sm" onClick={() => set({ rules: [...value.rules, { field: targets[2]?.id ?? '_title', op: opsFor(targets[2]?.kind ?? 'builtin')[0]! }] })} data-testid="add-condition">
            <Plus size={13} /> Add condition
          </button>
          {(value.in.length > 0 || value.rules.length > 0) && <button className="btn ghost sm" onClick={() => onChange({ in: [], match: 'all', rules: [] })}>Clear</button>}
        </div>
      </div>
    </div>
  );
}
